import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { AnimatePresence, MotionConfig, motion, useReducedMotion, type Variants } from "framer-motion";
import { CalendarCheck, CalendarPlus, X } from "lucide-react";
import { Layout } from "@/components/layout";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MonthFolder, folderTone, monthName } from "@/components/momentum/month-folder";
import { MomentumCard } from "@/components/momentum/momentum-card";
import { MomentumSheet } from "@/components/momentum/momentum-sheet";
import { useMomentum, useSetMomentumCompleted } from "@/hooks/use-momentum";
import { CalendarFlowProvider, useCalendarFlow } from "@/components/calendar/calendar-flow";
import { RoutineDoneDialog } from "@/components/calendar/routine-done-dialog";
import { showPillToast } from "@/components/calendar/pill-toast";
import { useRemoveSchedule } from "@/hooks/use-calendar";
import { upcomingEvents } from "@/lib/calendar";
import {
  applyOrder,
  groupMomentum,
  orderForOpening,
  progressLabel,
  type MomentumItem,
  type MomentumMonth,
} from "@/lib/momentum";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;
/** Cards after this many arrive together, so a long month never makes anyone wait. */
const STAGGER_CAP = 8;

/** Folder-grid columns, matching the grid's `grid-cols-2 sm:grid-cols-3 lg:grid-cols-4`. */
function useFolderColumns(): number {
  const read = () =>
    typeof window === "undefined"
      ? 4
      : window.matchMedia("(min-width: 1024px)").matches
        ? 4
        : window.matchMedia("(min-width: 640px)").matches
          ? 3
          : 2;
  const [cols, setCols] = useState(read);
  useEffect(() => {
    const queries = [window.matchMedia("(min-width: 1024px)"), window.matchMedia("(min-width: 640px)")];
    const onChange = () => setCols(read());
    queries.forEach((q) => q.addEventListener("change", onChange));
    return () => queries.forEach((q) => q.removeEventListener("change", onChange));
  }, []);
  return cols;
}

const SHEET_QUERY = "(max-width: 767px)";

/**
 * Phones (below `md`, where the bottom tab bar shows) open a month as a bottom sheet;
 * wider screens keep the inline panel. Read synchronously so a phone never paints a frame
 * of the inline panel first.
 */
function useSheetLayout(): boolean {
  const [sheet, setSheet] = useState(() => typeof window !== "undefined" && window.matchMedia(SHEET_QUERY).matches);
  useEffect(() => {
    const q = window.matchMedia(SHEET_QUERY);
    const onChange = () => setSheet(q.matches);
    onChange();
    q.addEventListener("change", onChange);
    return () => q.removeEventListener("change", onChange);
  }, []);
  return sheet;
}

function readMonthParam(): string | null {
  const m = new URLSearchParams(window.location.search).get("month");
  return m && /^\d{4}-\d{2}$/.test(m) ? m : null;
}

/** Keep the open folder in the URL (replace, not push), so Back from a journal returns to it. */
function writeMonthParam(key: string | null) {
  const url = new URL(window.location.href);
  if (key) url.searchParams.set("month", key);
  else url.searchParams.delete("month");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export default function MomentumScreen() {
  return (
    <CalendarFlowProvider origin="momentum">
      <MomentumContent />
    </CalendarFlowProvider>
  );
}

function MomentumContent() {
  const { data: items, isLoading, isError, refetch } = useMomentum();
  const setCompleted = useSetMomentumCompleted();
  const flow = useCalendarFlow();
  const removeSchedule = useRemoveSchedule();
  // Schedules outlive a disconnect, but removing their dates needs a live connection.
  const canRemoveDates = flow.connection?.status === "connected";
  const [routineDone, setRoutineDone] = useState<MomentumItem | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [orders, setOrders] = useState<Record<string, string[]>>({});
  const decidedInitial = useRef(false);
  const sheetLayout = useSheetLayout();

  const years = useMemo(() => groupMomentum(items ?? []), [items]);
  const months = useMemo(() => years.flatMap((y) => y.months), [years]);
  const hasAny = (items?.length ?? 0) > 0;

  const openMonth = useCallback(
    (key: string | null) => {
      if (key) {
        const m = months.find((x) => x.key === key);
        if (m) setOrders((prev) => ({ ...prev, [key]: orderForOpening(m.items) }));
      }
      setOpenKey(key);
      writeMonthParam(key);
    },
    [months],
  );

  // First load: reopen the folder in the URL, else (wider screens only) the newest month
  // with open steps. On a phone that would be a sheet over the page on arrival, so there a
  // fresh visit shows the folders and only a `?month=` (e.g. Back from a journal) reopens.
  useEffect(() => {
    if (decidedInitial.current || !items) return;
    decidedInitial.current = true;
    const fromUrl = readMonthParam();
    const initial =
      (fromUrl && months.some((m) => m.key === fromUrl) ? fromUrl : null) ??
      (window.matchMedia(SHEET_QUERY).matches
        ? null
        : [...months].sort((a, b) => b.key.localeCompare(a.key)).find((m) => m.items.some((i) => !i.completed))?.key) ??
      null;
    if (initial) openMonth(initial);
  }, [items, months, openMonth]);

  // Google's dates go only once the step is saved as done: if saving fails, the step is
  // unchanged and so is its calendar. Promises, not per-call callbacks — those are dropped
  // when another step is ticked before this one saves.
  const complete = useCallback(
    (item: MomentumItem, removeFutureDates: boolean) => {
      const month = months.find((m) => m.items.some((i) => i.id === item.id));
      setCompleted.mutateAsync({ item, completed: true }).then(
        () => {
          if (month) {
            showPillToast({
              message: `Step done · ${month.done + 1} of ${month.items.length} in ${monthName(month.month)}`,
              durationMs: 4000,
            });
          }
          if (removeFutureDates) {
            removeSchedule
              .mutateAsync({ actionId: item.id, scope: "future" })
              .catch(() => showPillToast({ message: "Couldn't remove the dates from Google Calendar · they're still there", tone: "warning" }));
          }
        },
        () => {}, // The hook already rolled back and said so.
      );
    },
    [setCompleted, removeSchedule, months],
  );

  // A routine with dates still ahead asks what happens to them before it's ticked off.
  const toggle = useCallback(
    (item: MomentumItem) => {
      if (item.completed) {
        setCompleted.mutate({ item, completed: false });
        return;
      }
      const schedule = flow.schedules.get(item.id);
      if (schedule?.kind === "rhythm" && upcomingEvents(schedule).length > 0) setRoutineDone(item);
      else complete(item, false);
    },
    [setCompleted, flow.schedules, complete],
  );

  return (
    <Layout>
      <MotionConfig reducedMotion="user">
        <div className="mx-auto w-full max-w-6xl px-5 py-8">
          <header className="mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div>
              <h1 className="page-title font-display text-3xl">Momentum</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="sm:hidden">Your Next Steps, month by month.</span>
                <span className="hidden sm:inline">
                  The Next Steps from your Unfoldings, gathered month by month — ready when you are.
                </span>
              </p>
            </div>
            <CalendarHeaderButton />
          </header>

          {isLoading ? (
            <FolderSkeleton />
          ) : isError ? (
            <div className="surface-card px-6 py-10 text-center">
              <p className="font-display text-xl font-semibold tracking-[-0.02em] text-foreground">
                Your Momentum didn&apos;t load
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Your steps are safe — this is just a connection hiccup.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className={cn(buttonVariants(), "mt-4 h-10 rounded-full px-5 text-sm")}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {!hasAny && <NoMomentumYet />}
              <div className="space-y-12">
                {years.map((y) => (
                  <section key={y.year} aria-labelledby={`momentum-year-${y.year}`}>
                    <h2
                      id={`momentum-year-${y.year}`}
                      className="mb-5 flex items-center gap-4 font-display text-2xl font-medium tracking-[-0.015em] text-foreground/85"
                    >
                      {y.year}
                      <span className="h-px flex-1 bg-foreground/[0.08]" />
                    </h2>
                    <FolderGrid
                      months={y.months}
                      openKey={openKey}
                      inlinePanel={!sheetLayout}
                      orders={orders}
                      onOpen={(key) => openMonth(openKey === key ? null : key)}
                      onToggle={toggle}
                    />
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
        {sheetLayout && (
          <MomentumSheet
            month={months.find((m) => m.key === openKey) ?? null}
            order={openKey ? orders[openKey] : undefined}
            onClose={() => openMonth(null)}
            onToggle={toggle}
          />
        )}
        <RoutineDoneDialog
          stepText={routineDone?.text ?? null}
          schedule={routineDone ? flow.schedules.get(routineDone.id) : undefined}
          canRemove={canRemoveDates}
          onCancel={() => setRoutineDone(null)}
          onKeep={() => {
            if (routineDone) complete(routineDone, false);
            setRoutineDone(null);
          }}
          onRemove={() => {
            if (routineDone) complete(routineDone, canRemoveDates);
            setRoutineDone(null);
          }}
        />
      </MotionConfig>
    </Layout>
  );
}

/** "Connect Calendar" until Google is connected; then a quiet link to its Settings. */
function CalendarHeaderButton() {
  const flow = useCalendarFlow();
  const className =
    "inline-flex items-center gap-1.5 rounded-full border border-foreground/12 bg-white px-3 py-[6px] text-sm font-medium text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
  if (flow.connection) {
    return (
      <Link href="/settings#calendar" className={className}>
        <CalendarCheck className="h-3.5 w-3.5 text-primary" />
        {flow.connection.status === "needs_reconnect" ? "Calendar needs reconnecting" : "Google Calendar connected"}
      </Link>
    );
  }
  return (
    <button type="button" onClick={flow.connect} className={className}>
      <CalendarPlus className="h-3.5 w-3.5" />
      Connect Calendar
    </button>
  );
}

function FolderGrid({
  months,
  openKey,
  inlinePanel,
  orders,
  onOpen,
  onToggle,
}: {
  months: MomentumMonth[];
  openKey: string | null;
  /** False on phones, where the open month shows as a sheet instead (see MomentumSheet). */
  inlinePanel: boolean;
  orders: Record<string, string[]>;
  onOpen: (key: string) => void;
  onToggle: (item: MomentumItem) => void;
}) {
  const cols = useFolderColumns();
  const rows: MomentumMonth[][] = [];
  for (let i = 0; i < months.length; i += cols) rows.push(months.slice(i, i + cols));

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-8">
      {rows.map((row, r) => {
        const openInRow = inlinePanel ? row.find((m) => m.key === openKey) : undefined;
        const col = openInRow ? row.indexOf(openInRow) : 0;
        return (
          <Fragment key={r}>
            {row.map((m) => (
              <MonthFolder
                key={m.key}
                month={m}
                open={m.key === openKey}
                onToggle={() => onOpen(m.key)}
                panelId={`momentum-panel-${m.key}`}
              />
            ))}
            <AnimatePresence mode="wait" initial={false}>
              {openInRow && (
                <MonthPanel
                  key={openInRow.key}
                  month={openInRow}
                  order={orders[openInRow.key]}
                  // Where the open folder sits across the row, 0..1 — the panel's notch
                  // and the cards' emergence both point back at it.
                  anchor={(col + 0.5) / cols}
                  onClose={() => onOpen(openInRow.key)}
                  onToggle={onToggle}
                />
              )}
            </AnimatePresence>
          </Fragment>
        );
      })}
    </div>
  );
}

function MonthPanel({
  month,
  order,
  anchor,
  onClose,
  onToggle,
}: {
  month: MomentumMonth;
  order: string[] | undefined;
  anchor: number;
  onClose: () => void;
  onToggle: (item: MomentumItem) => void;
}) {
  const reduce = useReducedMotion();
  // Read once, synchronously: the panel mounts fresh on every open, and its cards' very
  // first frame must already use the phone variant (useIsMobile reports false on mount).
  const [isMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const panelRef = useRef<HTMLDivElement>(null);
  const tone = folderTone(month);
  const items = applyOrder(month.items, order);
  const name = monthName(month.month);
  const total = month.items.length;

  // The panel opens below its row, which can be off-screen: if so, scroll its folder to
  // the top so the folder and its first cards are in view together.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const el = panelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const bottomInset = isMobile ? 96 : 24; // clear the floating mobile tab bar
      if (rect.top < 72 || rect.top > window.innerHeight - bottomInset - 160) {
        const folder = document.querySelector<HTMLElement>(`[data-momentum-folder="${month.key}"]`);
        (folder ?? el).scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
      }
    }, 140);
    return () => window.clearTimeout(t);
  }, [isMobile, reduce, month.key]);

  // Desktop: cards leave the folder slightly fanned and drift from its side of the row.
  // Mobile: a short vertical rise, no fan. Reduced motion: opacity only (MotionConfig
  // drops the transforms; the delays go too).
  const drift = (anchor - 0.5) * 2; // -1 (left) .. 1 (right)
  const cardVariants: Variants = {
    hidden: (i: number) =>
      reduce
        ? { opacity: 0 }
        : isMobile
          ? { opacity: 0, y: -10 }
          : {
              opacity: 0,
              y: -34,
              x: drift * 36,
              rotate: [-3, 2.5, -1.5, 3, -2][i % 5],
              scale: 0.94,
            },
    show: (i: number) => ({
      opacity: 1,
      y: 0,
      x: 0,
      rotate: 0,
      scale: 1,
      transition: reduce
        ? { duration: 0.15 }
        : {
            delay: 0.1 + Math.min(i, STAGGER_CAP) * (isMobile ? 0.03 : 0.055),
            type: "spring",
            stiffness: isMobile ? 520 : 420,
            damping: isMobile ? 40 : 32,
            mass: 0.7,
            opacity: { duration: 0.18, delay: 0.1 + Math.min(i, STAGGER_CAP) * (isMobile ? 0.03 : 0.055) },
          },
    }),
    exit: (i: number) => ({
      opacity: 0,
      y: reduce ? 0 : -12,
      scale: reduce ? 1 : 0.97,
      transition: { duration: 0.14, delay: reduce ? 0 : Math.min(i, 4) * 0.015 },
    }),
  };

  return (
    <motion.div
      ref={panelRef}
      id={`momentum-panel-${month.key}`}
      role="region"
      aria-label={`${name} ${month.year} Momentum`}
      className="col-span-full scroll-mt-20 overflow-hidden"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1, transition: { height: { duration: reduce ? 0.15 : 0.34, ease: EASE_OUT }, opacity: { duration: 0.18 } } }}
      exit={{ height: 0, opacity: 0, transition: { height: { duration: reduce ? 0.12 : 0.24, ease: EASE_OUT, delay: reduce ? 0 : 0.06 }, opacity: { duration: 0.2, delay: 0.04 } } }}
    >
      <div className="relative pt-3">
        {/* Notch pointing back up at the open folder */}
        <span
          aria-hidden
          className="absolute top-0 h-4 w-4 -translate-x-1/2 rotate-45 rounded-[3px]"
          style={{ left: `${anchor * 100}%`, background: tone.panel }}
        />
        <div
          className="relative rounded-3xl p-4 sm:p-6"
          style={{ background: `linear-gradient(180deg, ${tone.panel} 0%, rgb(248 246 242 / 0.9) 100%)` }}
        >
          <div className="mb-4 flex items-start justify-between gap-3 sm:mb-5">
            <div className="min-w-0">
              <h3 className="font-display text-2xl font-semibold leading-tight tracking-[-0.015em] text-foreground">
                {name} <span className="font-medium text-foreground/50">{month.year}</span>
              </h3>
              {total > 0 && (
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: tone.ink }}>
                  Your momentum · {progressLabel(month.done, total).toLowerCase()}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={`Close ${name}`}
              className="-mr-1 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/50 transition-colors hover:bg-white/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {total === 0 ? (
            <motion.p
              className="pb-1 text-sm leading-relaxed text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.1 } }}
              exit={{ opacity: 0 }}
            >
              {month.isCurrent ? (
                <>
                  Nothing gathered yet this month. The Next Steps from this month&apos;s Unfoldings will land here.{" "}
                  <Link href="/app" className="font-medium text-primary underline-offset-4 hover:underline">
                    Start an Unfolding
                  </Link>
                </>
              ) : (
                <>A quiet month — no Next Steps were gathered in {name}.</>
              )}
            </motion.p>
          ) : (
            <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {items.map((item, i) => (
                <motion.li
                  key={item.id}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  style={{ originY: 0 }}
                >
                  <MomentumCard item={item} onToggle={onToggle} />
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function NoMomentumYet() {
  return (
    <div className="surface-card mb-10 px-6 py-7 sm:px-8">
      <p className="eyebrow">Your momentum · your win</p>
      <h2 className="mt-2 font-display text-2xl font-semibold tracking-[-0.02em] text-foreground">
        Your Momentum will gather here
      </h2>
      <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
        Every Unfolding ends with Next Steps drawn from what you wrote. They&apos;ll collect here, month by month,
        so you can come back and act on them whenever you&apos;re ready.
      </p>
      <Link
        href="/app"
        className={cn(buttonVariants({ variant: "unfold" }), "mt-5 h-10 px-5 text-sm")}
      >
        Start an Unfolding
      </Link>
    </div>
  );
}

function FolderSkeleton() {
  return (
    <div aria-hidden className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex flex-col items-center pt-2">
          <div className="w-full rounded-[18px] bg-[#ece7df] motion-safe:animate-pulse" style={{ aspectRatio: "5 / 3.6" }} />
          <div className="mt-3 h-4 w-20 rounded bg-[#ece7df] motion-safe:animate-pulse" />
        </div>
      ))}
    </div>
  );
}
